import type { QueryClient, QueryFunction, QueryKey } from '@tanstack/react-query';
import { appointmentTypeAPI } from '@/modules/appointment/api/appointmentType.api';
import { dentalChairAPI } from '@/modules/clinic/api/dentalChair.api';
import { expenseCategoryAPI } from '@/modules/expense/api/expenseCategory.api';
import { incomeCategoryAPI } from '@/modules/income/api/incomeCategory.api';
import { kpiAPI } from '@/modules/kpi/api/kpi.api';
import { labMaterialAPI, labShadeAPI, labWorkTypeAPI } from '@/modules/labo/api/labCatalog.api';
import { listLabPartners } from '@/modules/labo/api/labPartner.api';
import { messagingAPI } from '@/modules/messaging/api/messaging.api';
import { listOvertimeTypes } from '@/modules/overtime/api/overtime.api';
import { fetchCustomerSources } from '@/modules/patient/api/customerSource.api';
import { paymentMethodAPI, type PaymentMethodUsageContext } from '@/modules/payment/api/paymentMethod.api';
import { payrollAPI } from '@/modules/payroll/api/payroll.api';
import { drugAPI } from '@/modules/prescription/api/drug.api';
import {
  ACTIVE_PRESCRIPTION_TEMPLATES_PARAMS,
  prescriptionTemplateAPI,
} from '@/modules/prescription/api/prescriptionTemplate.api';
import { listShiftTypes } from '@/modules/scheduling/api/shiftType.api';
import { listTimeOffTypes } from '@/modules/scheduling/api/timeOffType.api';
import { departmentAPI } from '@/modules/staff/api/department.api';
import { staffAPI } from '@/modules/staff/api/staff.api';
import { treatmentCatalogAPI } from '@/modules/treatment-catalog/api/treatmentCatalog.api';
import { treatmentCategoryAPI } from '@/modules/treatment-catalog/api/treatmentCategory.api';
import { treatmentUomAPI } from '@/modules/treatment-catalog/api/treatmentUom.api';
import { treatmentJournalAPI } from '@/modules/treatment-journal/api/treatmentJournal.api';
import { queryKeys } from './keys';
import { readPermissionsForMasterQuery } from './masterDataAccess';
import {
  applyMasterDataQueryDefaults,
  discardUnreadableMasterData,
  hydrateMasterData,
  type MasterDataReader,
} from './masterDataStore';

const ACTIVE_DRUGS_PARAMS = { limit: 1000, isActive: true } as const;

type CatalogQuery = {
  queryKey: QueryKey;
  queryFn: QueryFunction;
};

const paymentMethodQuery = (
  context: PaymentMethodUsageContext,
  includeInactive: boolean
): CatalogQuery => ({
  queryKey: queryKeys.payment.methods({ context, includeInactive }),
  queryFn: async () => (await paymentMethodAPI.getAll({ context, includeInactive })).data ?? [],
});

const staffRosterQuery = (clinicScope: string): CatalogQuery => ({
  queryKey: queryKeys.staff.roster(clinicScope),
  queryFn: () => staffAPI.getRoster(clinicScope),
});

/** Chairs and shift types follow the open clinic. Other catalogs do not. */
export const clinicMasterDataQueries = (clinicId: string | null): CatalogQuery[] => {
  if (!clinicId || clinicId === 'all') return [];
  return [
    {
      queryKey: queryKeys.clinic.chairs(clinicId, { includeInactive: false }),
      queryFn: async () => (await dentalChairAPI.getAll(clinicId, false)).data ?? [],
    },
    {
      queryKey: queryKeys.clinic.chairs(clinicId, { includeInactive: true }),
      queryFn: async () => (await dentalChairAPI.getAll(clinicId, true)).data ?? [],
    },
    {
      queryKey: queryKeys.scheduling.shiftTypes({ status: 'active', clinicId }),
      queryFn: () => listShiftTypes('active', { clinicId }),
    },
  ];
};

/** Picker lists warmed on app open. Same keys and shapes as the module queries. */
export const canonicalMasterDataQueries = (clinicId: string | null): CatalogQuery[] => {

  const paymentContexts: PaymentMethodUsageContext[] = [
    'payment',
    'income',
    'expense',
    'commission',
    'wallet_funding',
  ];

  return [
    {
      queryKey: queryKeys.appointment.types({ includeInactive: false }),
      queryFn: async () => (await appointmentTypeAPI.getAll(false)).data ?? [],
    },
    {
      queryKey: queryKeys.appointment.types({ includeInactive: true }),
      queryFn: async () => (await appointmentTypeAPI.getAll(true)).data ?? [],
    },
    {
      queryKey: queryKeys.treatmentCatalog.list({}),
      queryFn: async () => (await treatmentCatalogAPI.getAll()).data ?? [],
    },
    {
      queryKey: queryKeys.treatmentCatalog.categories({ includeInactive: false }),
      queryFn: async () => (await treatmentCategoryAPI.getAll(false)).data ?? [],
    },
    {
      queryKey: queryKeys.treatmentCatalog.categories({ includeInactive: true }),
      queryFn: async () => (await treatmentCategoryAPI.getAll(true)).data ?? [],
    },
    {
      queryKey: queryKeys.treatmentCatalog.uoms({ includeInactive: false }),
      queryFn: async () => (await treatmentUomAPI.getAll(false)).data ?? [],
    },
    {
      queryKey: queryKeys.treatmentCatalog.uoms({ includeInactive: true }),
      queryFn: async () => (await treatmentUomAPI.getAll(true)).data ?? [],
    },
    {
      queryKey: queryKeys.treatmentJournal.list({ includeInactive: false }),
      queryFn: async () => (await treatmentJournalAPI.getAll(false)).data ?? [],
    },
    {
      queryKey: queryKeys.treatmentJournal.list({ includeInactive: true }),
      queryFn: async () => (await treatmentJournalAPI.getAll(true)).data ?? [],
    },
    {
      queryKey: queryKeys.patient.customerSources({ includeInactive: false }),
      queryFn: () => fetchCustomerSources(false),
    },
    {
      queryKey: queryKeys.patient.customerSources({ includeInactive: true }),
      queryFn: () => fetchCustomerSources(true),
    },
    {
      queryKey: queryKeys.staff.departments({ includeInactive: false }),
      queryFn: async () => (await departmentAPI.getAll(false)).data ?? [],
    },
    {
      queryKey: queryKeys.staff.departments({ includeInactive: true }),
      queryFn: async () => (await departmentAPI.getAll(true)).data ?? [],
    },
    staffRosterQuery(clinicId ?? 'all'),
    {
      queryKey: queryKeys.expense.categories(),
      queryFn: async () => (await expenseCategoryAPI.getAll()).data ?? [],
    },
    {
      queryKey: queryKeys.expense.categories({ includeInactive: true }),
      queryFn: async () => (await expenseCategoryAPI.getAll(true)).data ?? [],
    },
    {
      queryKey: queryKeys.income.categories(),
      queryFn: async () => (await incomeCategoryAPI.getAll()).data ?? [],
    },
    {
      queryKey: queryKeys.income.categories({ includeInactive: true }),
      queryFn: async () => (await incomeCategoryAPI.getAll(true)).data ?? [],
    },
    ...paymentContexts.map((context) => paymentMethodQuery(context, false)),
    paymentMethodQuery('filter', true),
    {
      queryKey: queryKeys.payment.methods({ includeInactive: true }),
      queryFn: async () => (await paymentMethodAPI.getAll({ includeInactive: true })).data ?? [],
    },
    {
      queryKey: queryKeys.prescription.drugs({ ...ACTIVE_DRUGS_PARAMS }),
      queryFn: () => drugAPI.getDrugs({ ...ACTIVE_DRUGS_PARAMS }),
    },
    {
      queryKey: queryKeys.prescription.templates({ ...ACTIVE_PRESCRIPTION_TEMPLATES_PARAMS }),
      queryFn: () => prescriptionTemplateAPI.getAll({ ...ACTIVE_PRESCRIPTION_TEMPLATES_PARAMS }),
    },
    {
      queryKey: queryKeys.scheduling.timeOffTypes(),
      queryFn: async () => (await listTimeOffTypes()).data,
    },
    {
      queryKey: queryKeys.overtime.types({ status: 'active' }),
      queryFn: () => listOvertimeTypes({ status: 'active' }),
    },
    {
      queryKey: queryKeys.labo.workTypes({ includeInactive: false }),
      queryFn: async () => (await labWorkTypeAPI.getAll(false)).data ?? [],
    },
    {
      queryKey: queryKeys.labo.materials({ includeInactive: false }),
      queryFn: async () => (await labMaterialAPI.getAll(false)).data ?? [],
    },
    {
      queryKey: queryKeys.labo.shades({ includeInactive: false }),
      queryFn: async () => (await labShadeAPI.getAll(false)).data ?? [],
    },
    {
      queryKey: queryKeys.labo.workTypes({ includeInactive: true }),
      queryFn: async () => (await labWorkTypeAPI.getAll(true)).data ?? [],
    },
    {
      queryKey: queryKeys.labo.materials({ includeInactive: true }),
      queryFn: async () => (await labMaterialAPI.getAll(true)).data ?? [],
    },
    {
      queryKey: queryKeys.labo.shades({ includeInactive: true }),
      queryFn: async () => (await labShadeAPI.getAll(true)).data ?? [],
    },
    {
      queryKey: queryKeys.labo.activePartners,
      queryFn: () => listLabPartners({ status: 'active', limit: 100 }),
    },
    {
      queryKey: queryKeys.messaging.cannedResponses(),
      queryFn: async () => (await messagingAPI.listCannedResponses()).data || [],
    },
    {
      queryKey: queryKeys.messaging.cannedResponses({ includeInactive: true }),
      queryFn: async () =>
        (await messagingAPI.listCannedResponses({ includeInactive: true })).data || [],
    },
    {
      queryKey: queryKeys.payroll.components(false),
      queryFn: () => payrollAPI.listSalaryComponents(false),
    },
    {
      queryKey: queryKeys.payroll.components(true),
      queryFn: () => payrollAPI.listSalaryComponents(true),
    },
    {
      queryKey: queryKeys.kpi.templates(),
      queryFn: () => kpiAPI.getTemplates(),
    },
    {
      queryKey: queryKeys.kpi.templates({ includeInactive: true }),
      queryFn: () => kpiAPI.getTemplates({ includeInactive: true }),
    },
    ...clinicMasterDataQueries(clinicId),
  ];
};

const fetchCatalogQuery = async (queryClient: QueryClient, entry: CatalogQuery): Promise<void> => {
  try {
    await queryClient.fetchQuery({
      queryKey: entry.queryKey,
      queryFn: entry.queryFn,
      staleTime: 0,
    });
  } catch {
    // A 403 drops that user's saved copy in the query cache subscriber.
  }
};

const readableCatalog = (entries: CatalogQuery[], canRead: MasterDataReader): CatalogQuery[] =>
  entries.filter((entry) => {
    const required = readPermissionsForMasterQuery(entry.queryKey);
    return required ? canRead(required) : false;
  });

/** Leave free browser connections for the dashboard and org settings on login. */
const CATALOG_REFRESH_CONCURRENCY = 2;

const mapWithConcurrency = async (
  entries: CatalogQuery[],
  limit: number,
  run: (entry: CatalogQuery) => Promise<unknown>
): Promise<void> => {
  const queue = [...entries];
  const workers = Array.from({ length: Math.min(limit, queue.length) }, async () => {
    while (queue.length > 0) {
      const entry = queue.shift();
      if (!entry) return;
      await run(entry);
    }
  });
  await Promise.all(workers);
};

export const syncMasterDataQueries = async (
  queryClient: QueryClient,
  organizationId: string,
  userId: string,
  clinicId: string | null,
  canRead: MasterDataReader
): Promise<void> => {
  applyMasterDataQueryDefaults(queryClient);
  const restored = hydrateMasterData(queryClient, organizationId, userId);
  discardUnreadableMasterData(
    queryClient,
    organizationId,
    userId,
    canRead,
    undefined,
    restored
  );
  const canonical = readableCatalog(canonicalMasterDataQueries(clinicId), canRead);

  await mapWithConcurrency(canonical, CATALOG_REFRESH_CONCURRENCY, (entry) =>
    fetchCatalogQuery(queryClient, entry)
  );
};

/** Refresh only the lists that change with the selected clinic. */
export const syncClinicMasterDataQueries = async (
  queryClient: QueryClient,
  clinicId: string | null,
  canRead: MasterDataReader
): Promise<void> => {
  applyMasterDataQueryDefaults(queryClient);
  const queries = readableCatalog(
    [...clinicMasterDataQueries(clinicId), staffRosterQuery(clinicId ?? 'all')],
    canRead
  );
  await Promise.all(queries.map((entry) => fetchCatalogQuery(queryClient, entry)));
};
